import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyDuplicateCheck } from './fetchCompanyDuplicateCheck';
import {
  type CompanyDuplicate,
  type CompanyDuplicateCheckPayload,
} from './types';

/**
 * Generates the query options for checking a company for duplicates.
 *
 * @param payload - The identity fields of the company to be created. The query is skipped while undefined.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCompanyDuplicateCheckQueryOptions = <
  TData = CompanyDuplicate | null,
>({
  payload,
  options,
}: {
  payload: CompanyDuplicateCheckPayload | undefined;
  options?: Omit<
    UseQueryOptions<CompanyDuplicate | null, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: payload
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCompanyDuplicateCheck(payload, { signal })
      : skipToken,
    queryKey: queryKeysCompany.getDuplicateCheck(payload),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for checking whether creating a company with
 * the given identity fields would be rejected as a duplicate. The data is the
 * conflicting company, or `null` when there is none.
 * Requires ADMIN access.
 *
 * @param payload - The identity fields of the company to be created. The query is skipped while undefined.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_duplicate_vadmin_company_check_duplicate_post
 */
export const useQueryCompanyDuplicateCheck = <TData = CompanyDuplicate | null>(
  payload: CompanyDuplicateCheckPayload | undefined,
  options?: Omit<
    UseQueryOptions<CompanyDuplicate | null, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(getCompanyDuplicateCheckQueryOptions<TData>({ payload, options }));
